import React, { useEffect, useMemo, useRef, useState } from "react";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Chip,
  Grid,
} from "@material-ui/core";
import {
  Close,
  WhatsApp,
  Facebook,
  Instagram,
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import {
  FACEBOOK_OAUTH_SCOPES,
  INSTAGRAM_OAUTH_SCOPES,
  logFacebookOAuthInit,
} from "../../utils/facebookOAuthDebug";
import {
  INTEGRATION_CATALOG,
  isIntegrationUsable,
} from "../../utils/integrationCatalog";
import IntegrationChannelGrid, {
  IntegrationVisual,
} from "../IntegrationChannelGrid";

const CHANNEL_CATALOG = [
  {
    id: "whatsapp",
    group: "messaging",
    labelKey: "connections.addChannelModal.channels.whatsapp",
    planKey: "useWhatsapp",
    permissionKey: "whatsapp",
    available: true,
    visual: {
      bg: "#128C7E",
      Icon: WhatsApp,
      iconColor: "#fff",
    },
  },
  {
    id: "instagram",
    group: "messaging",
    labelKey: "connections.addChannelModal.channels.instagram",
    planKey: "useInstagram",
    permissionKey: "instagram",
    available: true,
    visual: {
      bg: "linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCAF45 100%)",
      Icon: Instagram,
      iconColor: "#fff",
    },
  },
  {
    id: "facebook",
    group: "messaging",
    labelKey: "connections.addChannelModal.channels.facebook",
    planKey: "useFacebook",
    permissionKey: "facebook",
    available: true,
    visual: {
      bg: "#1877F2",
      Icon: Facebook,
      iconColor: "#fff",
    },
  },
  {
    id: "tiktok",
    group: "social",
    labelKey: "connections.addChannelModal.channels.tiktok",
    available: false,
    visual: { brand: "tiktok" },
  },
  {
    id: "zoho",
    group: "email",
    labelKey: "connections.addChannelModal.channels.zoho",
    permissionKey: "email",
    available: true,
    visual: { brand: "zoho" },
  },
];

const FILTER_KEYS = ["all", "messaging", "social", "email", "integrations"];

const TikTokMark = ({ size = 52 }) => (
  <Box
    style={{
      width: "100%",
      height: "100%",
      background: "#010101",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width={size}
      height={size}
      viewBox="0 0 448 512"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#FFFFFF"
        d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"
      />
    </svg>
  </Box>
);

const ZohoMark = ({ size = 52 }) => (
  <Box
    style={{
      width: "100%",
      height: "100%",
      background: "#E42527",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
    }}
  >
    <svg width={size} height={size * 0.55} viewBox="0 0 120 48" aria-hidden="true">
      <rect x="4" y="6" width="112" height="36" rx="6" fill="#fff" fillOpacity="0.95" />
      <path fill="#E42527" d="M18 16h84v4H18v-4zm0 8h56v4H18v-4zm0 8h40v4H18v-4z" />
    </svg>
    <Typography
      style={{
        color: "#fff",
        fontWeight: 800,
        fontSize: size * 0.28,
        letterSpacing: "0.06em",
        lineHeight: 1,
      }}
    >
      ZOHO
    </Typography>
  </Box>
);

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 20,
      overflow: "hidden",
      maxHeight: "92vh",
      [theme.breakpoints.down("sm")]: {
        borderRadius: 0,
        margin: 0,
        width: "100%",
        maxWidth: "100%",
        height: "100%",
        maxHeight: "100%",
      },
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.5, 3, 2),
    borderBottom:
      theme.mode === "light"
        ? "1px solid rgba(15, 23, 42, 0.08)"
        : "1px solid rgba(148, 163, 184, 0.12)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2, 1.5, 1.5),
    },
  },
  headerText: { flex: 1, minWidth: 0 },
  title: { fontWeight: 700, fontSize: "1.125rem", lineHeight: 1.3 },
  subtitle: {
    marginTop: 2,
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
  },
  closeButton: { color: theme.palette.text.secondary },
  body: {
    overflowY: "auto",
    padding: theme.spacing(2.5, 3, 3),
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2, 1.5, 2.5),
      flex: 1,
    },
  },
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
  },
  filterChip: {
    fontWeight: 600,
    borderRadius: 20,
    height: 34,
    fontSize: "0.8125rem",
  },
  filterChipActive: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: "#fff !important",
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "1rem",
    marginBottom: theme.spacing(1.5),
  },
  integrationsSection: {
    marginTop: theme.spacing(3),
  },
  channelCard: {
    borderRadius: 16,
    overflow: "hidden",
    cursor: "pointer",
    border:
      theme.mode === "light"
        ? "1px solid rgba(15, 23, 42, 0.08)"
        : "1px solid rgba(148, 163, 184, 0.12)",
    backgroundColor: theme.palette.background.paper,
    transition: "transform 0.18s ease, box-shadow 0.18s ease",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow:
        theme.mode === "light"
          ? "0 12px 28px rgba(15, 23, 42, 0.12)"
          : "0 12px 28px rgba(0, 0, 0, 0.35)",
    },
  },
  channelCardDisabled: {
    cursor: "default",
    opacity: 0.5,
    "&:hover": {
      transform: "none",
      boxShadow: "none",
    },
  },
  channelVisual: {
    height: 120,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: { height: 100 },
  },
  channelFooter: {
    padding: theme.spacing(1.5, 1.5, 1.75),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    flex: 1,
  },
  channelName: { fontWeight: 600, fontSize: "0.9375rem", lineHeight: 1.3 },
  soonBadge: {
    alignSelf: "flex-start",
    height: 24,
    fontSize: "0.6875rem",
    fontWeight: 700,
    backgroundColor: "rgba(148, 163, 184, 0.18)",
    color: theme.palette.text.secondary,
  },
  hiddenOAuth: { display: "none" },
}));

const ChannelVisual = ({ visual, size = 56 }) => {
  if (visual.brand === "tiktok") return <TikTokMark size={size} />;
  if (visual.brand === "zoho") return <ZohoMark size={size} />;
  if (visual.brand === "openai" || visual.image) {
    return <IntegrationVisual visual={visual} size={size} />;
  }

  const Icon = visual.Icon;
  return (
    <Box
      style={{
        background: visual.bg,
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon style={{ color: visual.iconColor, fontSize: size }} />
    </Box>
  );
};

const isPlanFeatureEnabled = (plan, key) => {
  if (!plan || !key) return true;
  const value = plan[key];
  // Igual que PlansManager: solo se desactiva con false explícito.
  // null/undefined cuentan como habilitado.
  if (value === false || value === 0 || value === "0" || value === "false") {
    return false;
  }
  return true;
};

const normalizeFacebookLoginResponse = (response) => {
  if (!response) return { status: "unknown" };

  const auth = response.authResponse;
  if (auth?.accessToken) {
    return {
      ...response,
      ...auth,
      accessToken: auth.accessToken,
      id: auth.userID || auth.userId || response.id,
      userID: auth.userID || auth.userId,
      status: response.status || "connected",
      grantedScopes: auth.grantedScopes || response.grantedScopes,
    };
  }

  return response;
};

const AddChannelModal = ({
  open,
  onClose,
  canUseWhatsapp,
  canUseFacebook,
  canUseInstagram,
  canUseEmail,
  planConfig,
  onSelectWhatsapp,
  onSelectZoho,
  onFacebookConnect,
  onInstagramConnect,
  onSelectIntegration,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeFilter, setActiveFilter] = useState("all");

  const facebookLoginClickRef = useRef(null);
  const instagramLoginClickRef = useRef(null);

  const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID;

  const permissions = {
    whatsapp: canUseWhatsapp,
    facebook: canUseFacebook,
    instagram: canUseInstagram,
    email: canUseEmail,
  };

  const isChannelUsable = (channel) => {
    if (!channel.available) return false;
    if (channel.permissionKey && !permissions[channel.permissionKey]) return false;
    if (
      planConfig?.plan &&
      channel.planKey &&
      !isPlanFeatureEnabled(planConfig.plan, channel.planKey)
    ) {
      return false;
    }
    return true;
  };

  const visibleIntegrations = useMemo(
    () => INTEGRATION_CATALOG.filter((item) => isIntegrationUsable(item, planConfig)),
    [planConfig]
  );

  const filteredChannels = useMemo(() => {
    if (activeFilter === "integrations") return [];
    if (activeFilter === "all") return CHANNEL_CATALOG;
    return CHANNEL_CATALOG.filter((item) => item.group === activeFilter);
  }, [activeFilter]);

  const filteredIntegrations = useMemo(() => {
    if (activeFilter === "integrations" || activeFilter === "all") {
      return visibleIntegrations;
    }
    return [];
  }, [activeFilter, visibleIntegrations]);

  const availableFilters = useMemo(() => {
    const filters = FILTER_KEYS.filter((key) => {
      if (key === "integrations") return visibleIntegrations.length > 0;
      return true;
    });
    return filters;
  }, [visibleIntegrations]);

  useEffect(() => {
    if (!open) return;
    setActiveFilter("all");
  }, [open]);

  const handleClose = () => onClose();

  const startMetaOAuth = (channelId) => {
    if (!facebookAppId) {
      toast.error(
        i18n.t("connections.addChannelModal.missingFacebookAppId", {
          defaultValue:
            "Falta REACT_APP_FACEBOOK_APP_ID. Revisa la configuración del frontend.",
        })
      );
      return;
    }

    const scopes =
      channelId === "instagram" ? INSTAGRAM_OAUTH_SCOPES : FACEBOOK_OAUTH_SCOPES;
    const onConnect =
      channelId === "instagram" ? onInstagramConnect : onFacebookConnect;

    logFacebookOAuthInit(channelId, scopes);

    const finish = (response) => {
      handleClose();
      onConnect?.(normalizeFacebookLoginResponse(response));
    };

    // Preferir FB.login directo: más fiable que el botón oculto del SDK wrapper.
    if (typeof window !== "undefined" && window.FB?.login) {
      window.FB.login(
        (response) => finish(response),
        {
          scope: scopes,
          return_scopes: true,
          auth_type: "rerequest",
        }
      );
      return;
    }

    const trigger =
      channelId === "facebook"
        ? facebookLoginClickRef.current
        : instagramLoginClickRef.current;

    if (typeof trigger === "function") {
      trigger();
      return;
    }

    toast.error(
      i18n.t("connections.addChannelModal.facebookSdkNotReady", {
        defaultValue:
          "Facebook aún no está listo. Espera 2 segundos, abre de nuevo Conectores e intenta otra vez.",
      })
    );
  };

  const handleSelectChannel = (channel) => {
    if (!channel.available) {
      toast.info(i18n.t("connections.addChannelModal.comingSoonDesc"));
      return;
    }

    if (!isChannelUsable(channel)) {
      if (
        channel.planKey &&
        planConfig?.plan &&
        !isPlanFeatureEnabled(planConfig.plan, channel.planKey)
      ) {
        toast.error(
          i18n.t(`connections.plan.${channel.id}Disabled`, {
            defaultValue:
              "Este canal no está incluido en el plan de la empresa. Activa Facebook/Instagram en Planes.",
          })
        );
      } else {
        toast.error(
          i18n.t("connections.addChannelModal.noPermission", {
            defaultValue: "No tienes permiso para este canal",
          })
        );
      }
      return;
    }

    if (channel.id === "whatsapp") {
      handleClose();
      onSelectWhatsapp();
      return;
    }

    if (channel.id === "zoho") {
      handleClose();
      onSelectZoho();
      return;
    }

    if (channel.id === "facebook" || channel.id === "instagram") {
      startMetaOAuth(channel.id);
    }
  };

  const handleSelectIntegrationItem = (integration) => {
    if (!isIntegrationUsable(integration, planConfig)) {
      toast.error(i18n.t("connections.integrationsSection.planDisabled"));
      return;
    }
    handleClose();
    onSelectIntegration?.(integration);
  };

  const renderChannelGrid = (channels, sectionTitleKey) => (
    <>
      {sectionTitleKey && (
        <Typography className={classes.sectionTitle}>
          {i18n.t(sectionTitleKey)}
        </Typography>
      )}
      <Grid container spacing={2}>
        {channels.map((channel) => {
          const usable = isChannelUsable(channel);
          const isComingSoon = !channel.available;
          const isLocked = channel.available && !usable;

          return (
            <Grid item xs={6} sm={4} md={3} key={channel.id}>
              <Box
                className={`${classes.channelCard} ${
                  isLocked ? classes.channelCardDisabled : ""
                }`}
                onClick={() => handleSelectChannel(channel)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleSelectChannel(channel);
                  }
                }}
              >
                <Box className={classes.channelVisual}>
                  <ChannelVisual visual={channel.visual} />
                </Box>
                <Box className={classes.channelFooter}>
                  <Typography className={classes.channelName}>
                    {i18n.t(channel.labelKey)}
                  </Typography>
                  {isComingSoon && (
                    <Chip
                      size="small"
                      label={i18n.t("connections.addChannelModal.comingSoon")}
                      className={classes.soonBadge}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </>
  );

  const sectionTitleKey =
    activeFilter === "all"
      ? "connections.addChannelModal.sections.all"
      : `connections.addChannelModal.sections.${activeFilter}`;

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      className={classes.dialog}
      scroll="paper"
      keepMounted
    >
      <Box className={classes.header}>
        <Box className={classes.headerText}>
          <Typography className={classes.title}>
            {i18n.t("connections.addChannelModal.title")}
          </Typography>
          <Typography className={classes.subtitle}>
            {i18n.t("connections.addChannelModal.subtitleGallery")}
          </Typography>
        </Box>
        <IconButton
          aria-label={i18n.t("common.close")}
          onClick={handleClose}
          className={classes.closeButton}
        >
          <Close />
        </IconButton>
      </Box>

      <Box className={classes.body}>
        <Box className={classes.filterBar}>
          {availableFilters.map((key) => (
            <Chip
              key={key}
              label={i18n.t(`connections.addChannelModal.filters.${key}`)}
              clickable
              variant={activeFilter === key ? "default" : "outlined"}
              className={`${classes.filterChip} ${
                activeFilter === key ? classes.filterChipActive : ""
              }`}
              onClick={() => setActiveFilter(key)}
            />
          ))}
        </Box>

        {activeFilter !== "integrations" && filteredChannels.length > 0 && (
          renderChannelGrid(
            filteredChannels,
            activeFilter === "all" ? "connections.addChannelModal.sections.channels" : sectionTitleKey
          )
        )}

        {activeFilter === "integrations" && (
          <Typography className={classes.sectionTitle}>
            {i18n.t(sectionTitleKey)}
          </Typography>
        )}

        {filteredIntegrations.length > 0 && (
          <Box className={activeFilter === "all" ? classes.integrationsSection : undefined}>
            <IntegrationChannelGrid
              items={filteredIntegrations}
              isItemUsable={(item) => isIntegrationUsable(item, planConfig)}
              onSelect={handleSelectIntegrationItem}
              showTitle={activeFilter === "all"}
              titleKey="connections.addChannelModal.sections.integrations"
            />
          </Box>
        )}
      </Box>
    </Dialog>

      {/* SDK fuera del Dialog para que no se desmonte al cerrar y cancele el login */}
      <Box className={classes.hiddenOAuth}>
        {facebookAppId ? (
          <>
            <FacebookLogin
              appId={facebookAppId}
              autoLoad={false}
              fields="name,email,picture"
              version="18.0"
              returnScopes
              disableMobileRedirect
              scope={FACEBOOK_OAUTH_SCOPES}
              onClick={() => logFacebookOAuthInit("facebook", FACEBOOK_OAUTH_SCOPES)}
              callback={(response) => {
                handleClose();
                onFacebookConnect?.(response);
              }}
              render={(renderProps) => {
                facebookLoginClickRef.current = renderProps.onClick;
                return null;
              }}
            />
            <FacebookLogin
              appId={facebookAppId}
              autoLoad={false}
              fields="name,email,picture"
              version="18.0"
              returnScopes
              disableMobileRedirect
              scope={INSTAGRAM_OAUTH_SCOPES}
              onClick={() => logFacebookOAuthInit("instagram", INSTAGRAM_OAUTH_SCOPES)}
              callback={(response) => {
                handleClose();
                onInstagramConnect?.(response);
              }}
              render={(renderProps) => {
                instagramLoginClickRef.current = renderProps.onClick;
                return null;
              }}
            />
          </>
        ) : null}
      </Box>
    </>
  );
};

export default AddChannelModal;
